import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { TEnvironment } from '../Types/channel.types';
import { CopyIcon, DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import Button from 'Components/DesignSystem/Button/Button';
import Menu from 'Components/DesignSystem/Menu/Menu';
import Tag from 'Components/DesignSystem/Tag/Tag';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { MenuItem } from 'types/DesignSystem/Menu';
import { toast } from 'Components/DesignSystem/Toast/useToast';

/** The client-side cap the surrounding screen enforces. See the docblock. */
const MAX_ENVIRONMENTS = 3;

interface EnvironmentListProps {
    environments: TEnvironment[];
    isLoading: boolean;
    onAddEnvironment: () => void;
    onEditEnvironment: (environment: TEnvironment) => void;
    onDeleteEnvironment: (environment: TEnvironment) => void;
}

/**
 * The environments inside an expanded channel row (§ 120).
 *
 * **Deliberately a LIST, not a `Table`.** It reads as tabular, but its middle column
 * is the literal word "Key" repeated on every row — a column header living in the
 * cells — and the design has no header band above it, only the "Added Environment"
 * section label. `Table` always renders `TableHeader` (a table without column headers
 * is the thing that component exists to prevent), so forcing this through it would
 * either add a grey band the design does not have or produce a headerless table. A
 * `<ul>` of rows is what this actually is.
 *
 * What the swap fixes:
 *
 * - **Copying a key was mouse-only.** The control was a `<div role="button">` with no
 *   `tabIndex` — it announced itself as a button and could be neither tabbed to nor
 *   activated (SC 2.1.1). It is a real `Button` now, and its accessible name says
 *   *which* environment's key it copies, where before every row's control was
 *   identically named "Copy".
 * - **Index-templated testids.** `environment-environmentlist-div-1-${index}` and
 *   `-button-1-${index}` renamed rows on every sort or refresh; both key off the
 *   environment id now.
 * - **The row menu is portaled**, so it no longer depends on the ancestor chain
 *   staying `overflow: visible` — this list sits inside a table cell inside a
 *   horizontally scrolling wrapper.
 * - A dead `OverlayIcon` import is gone.
 *
 * **`MAX_ENVIRONMENTS = 3` is a client-side cap the backend does not enforce** — a
 * channel in the dev data holds nine. Extracted to a constant here rather than left
 * as a bare `3`, but the value and the behaviour are unchanged: this list hides its
 * own Add button at or above the cap, and `ChannelTable` shows "(Max limit reached)".
 * Whether the cap or the copy is wrong is a product question, flagged not answered.
 */
const EnvironmentList: React.FC<EnvironmentListProps> = ({
    environments,
    isLoading,
    onAddEnvironment,
    onEditEnvironment,
    onDeleteEnvironment
}) => {
    const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

    useEffect(() => {
        function handleScroll() {
            setOpenMenuFor(null);
        }
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    const getMenuItems = (environment: TEnvironment): MenuItem[] => [
        {
            id: `edit-env-${environment.id}`,
            label: 'Edit',
            icon: <EditIcon />,
            'data-testid': `environment-menu-edit-${environment.id}`,
            onClick: () => { setOpenMenuFor(null); onEditEnvironment(environment); },
        },
        /* A standalone divider entry, not `divider: true` on Delete — the DS `Menu`
           renders the rule INSTEAD of the item it is flagged on (§ 118). */
        { id: `divider-env-${environment.id}`, divider: true },
        {
            id: `delete-env-${environment.id}`,
            label: 'Delete',
            icon: <DeleteIcon />,
            'data-testid': `environment-menu-delete-${environment.id}`,
            onClick: () => { setOpenMenuFor(null); onDeleteEnvironment(environment); },
        },
    ];

    const handleCopyKey = (environment: TEnvironment) => {
        if (!environment.key) return;
        navigator.clipboard.writeText(environment.key).then(() => {
            toast.success('Key copied to clipboard!');
        }).catch(() => {
            toast.error('Failed to copy key');
        });
    };

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>, environmentId: string) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        setOpenMenuFor(prev => (prev === environmentId ? null : environmentId));
    };

    if (isLoading) {
        return (
            <div className="tw-flex tw-justify-center tw-py-6" data-testid="environment-list-loading">
                <Spinner size="sm" label="Loading environments" showLabel />
            </div>
        );
    }

    return (
        <div className="tw-px-4">
            <Typography
                variant="sub-title-label"
                as="div"
                className="tw-text-ink-tertiary tw-px-4 tw-pt-4 tw-pb-2"
                data-testid="environment-list-heading"
            >
                Added Environment
            </Typography>

            <ul className="tw-list-none tw-m-0 tw-p-0 tw-border-b tw-border-edge" data-testid="environment-list">
                {environments.map((env) => (
                    <li
                        key={env.id}
                        className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3"
                        data-testid={`environment-row-${env.id}`}
                    >
                        <div className="tw-w-[30%] tw-flex tw-items-center tw-gap-3 tw-min-w-0">
                            <Typography
                                variant="title"
                                as="span"
                                className="tw-text-ink-secondary tw-truncate"
                                data-testid={`environment-name-${env.id}`}
                            >
                                {env.name}
                            </Typography>
                            {env.isProduction && (
                                /* `meta`, not `selected`: this DESCRIBES the environment,
                                   it is not a choice the user made in this list (§ 66). */
                                <Tag
                                    label="Production"
                                    variant="meta"
                                    data-testid={`environment-production-tag-${env.id}`}
                                />
                            )}
                        </div>

                        <div className="tw-w-[20%] tw-flex tw-items-center tw-gap-2">
                            <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">
                                Key
                            </Typography>
                            <Button
                                variant="subtle"
                                size="sm"
                                /* Names the environment, not just the verb: nine rows of
                                   "Copy" tell a screen-reader user nothing about which
                                   key they are about to put on the clipboard. */
                                aria-label={`Copy ${env.name} key`}
                                data-testid={`environment-copy-key-${env.id}`}
                                onClick={() => handleCopyKey(env)}
                            >
                                <CopyIcon />
                            </Button>
                        </div>

                        <div className="tw-flex-1 tw-flex tw-justify-end">
                            <button
                                type="button"
                                aria-label={`More options for ${env.name}`}
                                aria-haspopup="menu"
                                aria-expanded={openMenuFor === env.id}
                                data-testid={`environment-overflow-${env.id}`}
                                className="tw-cursor-pointer tw-rounded-ctl tw-p-1 hover:tw-bg-surface"
                                onClick={(e) => handleOverflowClick(e, env.id)}
                            >
                                <OverFlowIcon />
                            </button>
                            {openMenuFor === env.id ? ReactDOM.createPortal(
                                <div
                                    className="tw-fixed tw-z-[9999] tw-w-48"
                                    style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                                >
                                    <Menu
                                        variant="compact"
                                        items={getMenuItems(env)}
                                        data-testid={`environment-menu-${env.id}`}
                                        onDismiss={() => setOpenMenuFor(null)}
                                    />
                                </div>,
                                document.body
                            ) : null}
                        </div>
                    </li>
                ))}
            </ul>

            {environments.length < MAX_ENVIRONMENTS ? (
                <div className="tw-px-4 tw-pb-4 tw-mt-2">
                    <Button
                        variant="secondary"
                        onClick={onAddEnvironment}
                        data-testid="environment-list-add-btn"
                    >
                        Add Environment
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

export default EnvironmentList;
